import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Button } from '@shared/components/ui/button';
import { Spinner } from '@shared/components/ui/Spinner';
import { Incident, IncidentActionsResponse } from '../../models/Incident';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faBolt } from '@fortawesome/pro-light-svg-icons';
import { SolveIncidentModal, SolveIncidentModalRef } from './SolveIncidentModal';
import { useIncident } from '../../hooks/useIncidents';

export interface ActionsModalRef {
  openWithIncident: (incident: Incident) => void;
  close: () => void;
}

interface ActionsModalProps {
  onSuccess?: () => void;
}

export const ActionsModal = forwardRef<ActionsModalRef, ActionsModalProps>(({ onSuccess }, ref) => {
  const { t } = useTranslation('incidentsModule');
  const modalRef = useRef<ModalCenterRef>(null);
  const solveModalRef = useRef<SolveIncidentModalRef>(null);
  const [currentIncident, setCurrentIncident] = useState<Incident | null>(null);

  // Obtener el detalle de la incidencia que incluye acciones_elegibles
  // La llamada se ejecuta cuando se abre la modal (cuando currentIncident?.id está disponible)
  const { data: incidentDetail, isLoading } = useIncident(currentIncident?.id ?? null);

  // Extraer acciones_elegibles del detalle
  const actions: IncidentActionsResponse | undefined = incidentDetail?.acciones_elegibles
    ? {
        items: incidentDetail.acciones_elegibles.items || [],
        fallback: incidentDetail.acciones_elegibles.fallback || false,
      }
    : undefined;

  const isResolved = currentIncident?.status === 'resolved';
  const hasActions = actions && actions.items && actions.items.length > 0;
  const isFallback = actions?.fallback || false;

  // Estado para controlar cuándo se debe abrir la modal
  const [shouldOpen, setShouldOpen] = useState(false);

  // Abrir la modal cuando currentIncident esté establecido y modalRef esté disponible
  // Usamos requestAnimationFrame para asegurar que el componente se haya renderizado
  useEffect(() => {
    if (shouldOpen && currentIncident) {
      let attempts = 0;
      const maxAttempts = 10; // Máximo 10 intentos (aprox. 160ms a 60fps)

      const tryOpen = () => {
        attempts++;
        if (modalRef.current) {
          modalRef.current.openModal();
          setShouldOpen(false); // Reset flag
        } else if (attempts < maxAttempts) {
          // Si aún no está disponible y no hemos excedido el límite, intentar de nuevo
          requestAnimationFrame(tryOpen);
        } else {
          setShouldOpen(false); // Reset flag para evitar loops infinitos
        }
      };

      // Intentar abrir en el siguiente frame de animación
      requestAnimationFrame(tryOpen);
    }
  }, [shouldOpen, currentIncident]);

  useImperativeHandle(
    ref,
    () => ({
      openWithIncident: (incident: Incident) => {
        // Establecer el incidente primero
        setCurrentIncident(incident);

        // Si el ref ya está disponible, abrir inmediatamente
        if (modalRef.current) {
          modalRef.current.openModal();
        } else {
          // Si no está disponible, marcar que debe abrirse cuando el ref esté listo
          setShouldOpen(true);
        }
      },
      close: () => {
        modalRef.current?.closeModal();
        setCurrentIncident(null);
        setShouldOpen(false);
      },
    }),
    [],
  );

  const handleExecute = (actionId: number) => {
    solveModalRef.current?.open(actionId);
  };

  if (!currentIncident) return null;

  return (
    <>
      <ModalCenter ref={modalRef} title={t('detail.actions.title')} width="lg" type="default">
        <div className="space-y-4">
          {/* Banner si es fallback */}
          {isFallback && (
            <div className="p-3 bg-bg-warning rounded-lg flex items-center gap-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 text-warning" />
              <span className="text-sm text-text flex-1">{t('detail.actions.fallback_message')}</span>
            </div>
          )}

          {/* Lista de acciones */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : hasActions ? (
            <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
              {actions.items.map((action) => (
                <li
                  key={action.id}
                  className="flex items-center justify-between p-3 bg-body-bg rounded-lg border border-card-border"
                >
                  <span className="text-sm text-text flex-1">{action.name}</span>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleExecute(action.id)}
                    disabled={isResolved}
                    data-testid={`action-button-${action.id}`}
                  >
                    {t('detail.actions.execute')}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-complementary-text  text-center py-6 rounded border-card-border border bg-card-bg my-3">
              {t('detail.actions.no_actions')}
            </p>
          )}
        </div>
      </ModalCenter>

      {/* Modal de resolver */}
      {currentIncident && (
        <SolveIncidentModal
          ref={solveModalRef}
          incident={currentIncident}
          actions={actions}
          onSuccess={() => {
            onSuccess?.();
            modalRef.current?.closeModal();
          }}
        />
      )}
    </>
  );
});

ActionsModal.displayName = 'ActionsModal';
